// API client for backend communication
/* eslint-disable no-unused-vars, no-undef, no-useless-catch, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any, no-unreachable */

import axios from 'axios';
import { createAxiosInstance } from '../services/apiInterceptor';
import { getCurrentSession } from './db';
import { ENV } from '../utils/env';

/* eslint-enable no-unused-vars, no-undef */

export const API_URL = ENV.VITE_API_URL;

// Add cache control headers to prevent 304
const defaultHeaders = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache',
  'Expires': '0'
};

const buildAuthHeaders = (token?: string): Record<string, string> => {
  if (token && token.trim().length > 0) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

/* eslint-disable no-undef */
// Helper function to handle API responses
const handleResponse = async (response: Response) => {
/* eslint-enable no-undef */
  try {
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `Request failed with status: ${response.status}`;
      } catch {
        // Special handling for 404 errors
        if (response.status === 404) {
          // Use the browser's URL constructor
          const url = new URL(response.url);
          const path = url.pathname;
          errorMessage = `Resource not found: ${path} (404)`;
        } else {
          errorMessage = `Request failed with status: ${response.status}`;
        }
      }

      throw new Error(errorMessage);
    }

    // For successful responses, try to parse JSON
    try {
      const data = await response.json();
      return data;
    } catch (error) {
      // Return true for 204 No Content
      if (response.status === 204) {
        return true;
      }
      throw new Error('Invalid JSON response from server');
    }
  } catch (error) {
    throw error;
  }
};

export interface Post {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  published: boolean;
  created_at: string;
  updated_at?: string;
  featured_image?: string;
  author?: string;
  author_avatar?: string;
  tags?: string[];
  image_caption?: string;
  image_credit?: string;
}

export interface User {
  id: string;
  email: string;
  role: string;
}

interface ValidateSessionResponse {
  id: string;
  email: string;
  role: string;
}

export interface NotificationConfig {
  recipients?: string[];
  subject_template?: string;
  from_email?: string;
  webhook_url?: string;
  channel?: string;
  username?: string;
}

export interface NotificationSetting {
  id: string;
  type: 'email' | 'slack';
  name: string;
  enabled: boolean;
  config: NotificationConfig;
  description: string;
  created_at: string;
  updated_at?: string;
}

// Authentication
export const login = async (email: string, password: string) => {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  return handleResponse(response);
};

export const verify2FA = async (challengeToken: string, token: string) => {
  const response = await fetch(`${API_URL}/api/auth/2fa/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ challengeToken, token }),
  });

  return handleResponse(response);
};

export const validateSession = async (token?: string): Promise<ValidateSessionResponse> => {
  try {
    const headers: Record<string, string> = {
      ...defaultHeaders
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/auth/validate`, {
      credentials: 'include',
      headers
    });

    return handleResponse(response);
  } catch (error) {
    throw error;
  }
};

// Public blog posts - Note: This might return 404 if the public endpoint doesn't exist
export const getPosts = async () => {
  try {
    const response = await fetch(`${API_URL}/api/posts`, {
      headers: defaultHeaders
    });
    
    return handleResponse(response);
  } catch (error) {
    throw error;
  }
};

export const getPost = async (slug: string) => {
  const startTime = performance.now();
  try {
    const response = await fetch(`${API_URL}/api/posts/${slug}`, {
      headers: defaultHeaders
    });
    
    const result = await handleResponse(response);
    return result;
  } catch (error) {
    throw error;
  }
};

// Add getPostBySlug as a wrapper around getPost for better semantic naming
export const getPostBySlug = async (slug: string): Promise<Post> => {
  return getPost(slug);
};

// Helper function to retry a fetch operation
const fetchWithRetry = async (url: string, options: RequestInit, retries = 3, delay = 1000): Promise<Response> => {
  try {
    return await fetch(url, options);
  } catch (err) {
    if (retries <= 1) throw err;
    await new Promise(resolve => setTimeout(resolve, delay));
    return fetchWithRetry(url, options, retries - 1, delay * 2);
  }
};

// Admin operations for posts
export const getAdminPosts = async (token?: string): Promise<Post[]> => {
  try {
    const response = await fetch(`${API_URL}/api/admin/posts`, {
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        'Content-Type': 'application/json',
        ...buildAuthHeaders(token)
      }
    });

    const data = await handleResponse(response);
    return data.posts || []; // Extract just the posts array from the response
  } catch (error) {
    throw error;
  }
};

export const getAdminPost = async (token: string | undefined, id: string): Promise<Post> => {
  try {
    const endpoint = `${API_URL}/api/admin/posts/${id}`;
    
    // Use the retry mechanism
    const response = await fetchWithRetry(endpoint, {
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        'Content-Type': 'application/json',
        ...buildAuthHeaders(token)
      }
    }, 3, 1000);

    if (response.status === 404) {
      throw new Error(`Post with ID ${id} not found. It may have been deleted or never existed.`);
    }

    return handleResponse(response);
  } catch (error) {
    throw error;
  }
};

export const updatePost = async (token: string | undefined, id: string, post: Partial<Post>) => {
  try {
    const endpoint = `${API_URL}/api/admin/posts/${id}`;
    
    const response = await fetch(endpoint, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        'Content-Type': 'application/json',
        ...buildAuthHeaders(token)
      },
      body: JSON.stringify(post),
    });
    
    if (response.status === 404) {
      throw new Error(`Post with ID ${id} not found for update. It may have been deleted or never existed.`);
    }

    return handleResponse(response);
  } catch (error) {
    throw error;
  }
};

export const deletePost = async (token: string | undefined, id: string) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/posts/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        ...buildAuthHeaders(token)
      },
    });

    return handleResponse(response);
  } catch (error) {
    throw error;
  }
};

export const createPost = async (token: string | undefined, post: Partial<Post>) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/posts`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        'Content-Type': 'application/json',
        ...buildAuthHeaders(token)
      },
      body: JSON.stringify(post),
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    throw error;
  }
};

// Function to check if the API server is running and accessible
export const checkApiHealth = async (): Promise<{ isHealthy: boolean; status?: number; message?: string }> => {
  try {
    // Try the health endpoint first
    try {
      const healthResponse = await fetch(`${API_URL}/api/health`, {
        method: 'GET',
        headers: defaultHeaders
      });
      
      if (healthResponse.ok) {
        return { isHealthy: true, status: healthResponse.status, message: 'API server is healthy' };
      }
    } catch (healthError) {
      // Silent fail
    }
    
    // If health endpoint fails, try a simple GET request to the root
    try {
      const rootResponse = await fetch(`${API_URL}/api`, {
        method: 'GET',
        headers: defaultHeaders
      });
      
      if (rootResponse.ok || rootResponse.status === 404) {
        // Even a 404 means the server is running, just not that specific endpoint
        return { 
          isHealthy: true, 
          status: rootResponse.status, 
          message: 'API server is responding (no health endpoint)' 
        };
      }
      
      return { 
        isHealthy: false, 
        status: rootResponse.status, 
        message: `API server returned status ${rootResponse.status}` 
      };
    } catch (rootError) {
      return { 
        isHealthy: false, 
        message: 'API server is not responding to any requests' 
      };
    }
  } catch (error) {
    return { 
      isHealthy: false, 
      message: error instanceof Error ? error.message : 'Unknown error checking API health' 
    };
  }
};

// Testimonial API functions
export interface PublicTestimonial {
  id: string;
  company_name: string;
  representative_name: string;
  position?: string | null;
  review: string;
  logo_url?: string | null;
}

export const getPublicTestimonials = async (): Promise<PublicTestimonial[]> => {
  try {
    const response = await fetch(`${API_URL}/api/testimonials`);
    if (!response.ok) return [];
    return await response.json();
  } catch {
    return [];
  }
};

export const getTestimonials = async (token?: string) => {
  const response = await fetch(`${API_URL}/api/admin/testimonials`, {
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
    },
  });
  const data = await handleResponse(response);
  return data.testimonials || []; // Extract just the testimonials array from the response
};

export const createTestimonial = async (token: string | undefined, data: any) => {
  const response = await fetch(`${API_URL}/api/admin/testimonials`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const updateTestimonial = async (token: string | undefined, id: string, data: any) => {
  const response = await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const deleteTestimonial = async (token: string | undefined, id: string) => {
  const response = await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
    },
  });
  return handleResponse(response);
};

// Consultation API functions
export const getConsultationRequests = async (token?: string) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/consultation-requests`, {
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        'Content-Type': 'application/json',
        ...buildAuthHeaders(token),
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteConsultationRequest = async (token: string | undefined, id: string) => {
  const response = await fetch(`${API_URL}/api/admin/consultation-requests/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
    },
  });
  return handleResponse(response);
};

export const updateConsultationStatus = async (
  token: string | undefined, 
  id: string, 
  newStatus: 'approved' | 'rejected', 
  admin_notes?: string,
  department: string = 'sales',
  converted_to_customer?: boolean
) => {
  // Map frontend status to backend status
  const statusMap = {
    'approved': 'in-progress',
    'rejected': 'cancelled'
  };

  const response = await fetch(`${API_URL}/api/admin/consultation-requests/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      status: statusMap[newStatus],
      admin_notes,
      department,
      converted_to_customer
    }),
  });
  return handleResponse(response);
};

// Notification API functions
export const getNotificationSettings = async (token?: string): Promise<NotificationSetting[]> => {
  const response = await fetch(`${API_URL}/api/admin/notification-settings`, {
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
    },
  });
  const data = await handleResponse(response);
  return data.settings || []; // Extract just the settings array from the response
};

export const updateNotificationSettings = async (token: string | undefined, settings: NotificationSetting[]) => {
  const response = await fetch(`${API_URL}/api/admin/notification-settings`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(settings),
  });
  return handleResponse(response);
};

export const testNotification = async (token: string | undefined, type: 'email' | 'slack') => {
  const response = await fetch(`${API_URL}/api/admin/test-notification`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ type }),
  });
  return handleResponse(response);
};

// Add other API functions as needed 
